import { useState } from "react";

type TabKey = "border" | "vehicle" | "license" | "preparation";

interface InfoItem {
  icon: string;
  title: string;
  desc: string;
}

interface Step {
  step: string;
  title: string;
  desc: string;
}

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "border", label: "跨境手續", icon: "ri-passport-line" },
  { key: "vehicle", label: "車輛要求", icon: "ri-car-line" },
  { key: "license", label: "國際駕照", icon: "ri-id-card-line" },
  { key: "preparation", label: "行前準備", icon: "ri-checkbox-circle-line" },
];

const borderSteps: Step[] = [
  {
    step: "01",
    title: "出發口岸",
    desc: "所有路線統一使用深圳灣口岸出發，全天24小時通關，車輛專用車道手續快捷流暢。我們會在出發前確認通關時間，確保行程順利展開。",
  },
  {
    step: "02",
    title: "申請車輛臨時入境許可",
    desc: "港澳私家車進入內地須事先向廣東省公安廳申請「臨時入境牌照」。我們為客人代辦全程手續，包括批文申請、臨時牌照、海關保證金安排，費用已包含於行程報價。",
  },
  {
    step: "03",
    title: "準備過關文件",
    desc: "駕駛人需備齊：香港身份證 / 回鄉證（港人）、香港車輛登記文件、有效保險單、駕照（香港駕照）及臨時牌照批准書。我們提供完整文件清單及核查服務。",
  },
  {
    step: "04",
    title: "出入境申報",
    desc: "車輛過境時需按海關要求填寫車輛申報表，申報隨行物品。部分路線涉及特殊地區（如西藏、新疆）須提前辦理「邊境通行證」，我們協助統一辦理。",
  },
  {
    step: "05",
    title: "歐洲跨境手續",
    desc: "歐洲路線需準備「車輛國際通行證」(Carnet de Passage)、綠卡保險 (Green Card)、反光背心及三角警示牌。部分過境國家另需申請簽注，詳情由專屬顧問按路線逐一安排。",
  },
];

const vehicleItems: InfoItem[] = [
  {
    icon: "ri-car-washing-line",
    title: "車型選擇",
    desc: "山地及高原路線建議使用四驅越野車（SUV / 4WD）。雪地、冰島路段須配備全季或雪地胎。平原及歐洲公路普通轎車或SUV均可。",
  },
  {
    icon: "ri-tools-line",
    title: "車輛檢查",
    desc: "出發前須完成全面保養：機油、剎車、輪胎胎壓、備胎、車燈、冷卻液。長途自駕建議攜帶備用輪胎兩條、充氣泵及補胎套裝。",
  },
  {
    icon: "ri-shield-check-line",
    title: "保險要求",
    desc: "中國大陸路段須持有效「交強險」及商業三責險，我們為客人統一安排。歐洲路段需購買涵蓋所有過境國的「綠卡保險」，最低保障金額按各國法規不同。",
  },
  {
    icon: "ri-signal-tower-line",
    title: "通訊設備",
    desc: "建議攜帶衛星通訊設備（如 Garmin inReach），在無網絡信號的高原、峽谷地帶保持緊急通訊能力。我們的部分路線提供衛星電話租借服務。",
  },
  {
    icon: "ri-gas-station-line",
    title: "油料準備",
    desc: "西藏、新疆、冰島等偏遠路段加油站間距可逾200公里，須備備用油桶（20-40公升）。我們的路線規劃已詳細標注加油站分佈，確保無斷油風險。",
  },
  {
    icon: "ri-windy-line",
    title: "高原適應",
    desc: "4000米以上高原路段，車輛動力下降約30-50%，化油器版本車輛需提前調整。建議攜帶備用氧氣罐，我們每位客人標配提供氧氣瓶。",
  },
];

const licenseSteps: Step[] = [
  {
    step: "01",
    title: "香港駕照適用範圍",
    desc: "持香港駕照在港行駛無問題。進入中國大陸，目前須持有大陸駕照或換領臨時駕照，我們代辦「臨時駕照」服務，費用已含於行程。",
  },
  {
    step: "02",
    title: "國際駕駛執照 (IDP)",
    desc: "歐洲、中亞等地需申請「國際駕駛執照」(International Driving Permit)。香港居民可向香港汽車會或香港駕駛學院申請，收費約港幣150元，辦理時間2-3個工作日。",
  },
  {
    step: "03",
    title: "IDP 申請所需文件",
    desc: "申請 IDP 需提供：香港身份證（正副本）、有效香港駕照（正副本）、近照兩張（35mm x 45mm）及申請費。IDP 有效期 12 個月，必須與香港駕照同時使用。",
  },
  {
    step: "04",
    title: "1949年《道路交通公約》IDP",
    desc: "香港簽發的 IDP 依據 1949 年《日內瓦道路交通公約》，被大多數歐洲國家及亞洲國家認可。部分中亞國家可能要求公證翻譯本，我們顧問會提前確認各路線要求。",
  },
  {
    step: "05",
    title: "我們的代辦服務",
    desc: "我們提供完整駕照代辦諮詢服務，包括：臨時大陸駕照申請、IDP 申請輔助、各過境國駕照認可確認。客人無需自行處理複雜的跨境駕照問題。",
  },
];

const prepItems: { category: string; icon: string; color: string; items: string[] }[] = [
  {
    category: "文件清單",
    icon: "ri-file-list-3-line",
    color: "bg-blue-50 text-blue-600",
    items: [
      "香港身份證 / 回鄉證（正副本）",
      "香港駕照 + 國際駕駛執照",
      "車輛登記文件及臨時牌照",
      "保險文件（交強險 / 綠卡）",
      "各段路線批文及通行許可",
      "緊急聯絡人資料",
    ],
  },
  {
    category: "應急裝備",
    icon: "ri-first-aid-kit-line",
    color: "bg-red-50 text-red-600",
    items: [
      "急救藥包（含高原反應藥物）",
      "備用輪胎 × 2 及充氣泵",
      "牽引繩及車輛工具套裝",
      "反光背心 × 人數（歐洲必備）",
      "三角警示牌 × 2",
      "衛星通訊設備或衛星電話",
    ],
  },
  {
    category: "高原必備",
    icon: "ri-mountain-line",
    color: "bg-amber-50 text-amber-600",
    items: [
      "氧氣瓶（我們標配每人2瓶）",
      "高原反應藥物（紅景天、乙酰唑胺）",
      "防曬霜 SPF50+（高原UV極強）",
      "保暖內衣及防風外套",
      "保溫水壺（防止脫水）",
      "能量補給食品（巧克力、堅果）",
    ],
  },
  {
    category: "導航與通訊",
    icon: "ri-navigation-line",
    color: "bg-green-50 text-green-600",
    items: [
      "離線地圖下載（Maps.me / 高德）",
      "行車記錄儀（建議前後雙鏡頭）",
      "充電寶 × 2（大容量20000mAh+）",
      "多國SIM卡或國際漫遊套餐",
      "車載逆變器（220V供電）",
      "太陽能充電板（偏遠地區備用）",
    ],
  },
];

const SelfDriveInfoSection = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("border");

  return (
    <section id="info" className="py-24 bg-stone-50">
      <div className="w-full px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase mb-4 block">
            自駕實用資訊
          </span>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            出發前必讀指南
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            由香港自駕出境涉及多項手續，我們為您詳細說明每個環節，讓旅程萬無一失
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* Tab Bar */}
          <div className="flex border-b border-stone-100 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-7 py-5 text-sm font-semibold border-b-2 transition-all cursor-pointer whitespace-nowrap flex-1 justify-center ${
                  activeTab === tab.key
                    ? "border-orange-500 text-orange-600 bg-orange-50/40"
                    : "border-transparent text-gray-500 hover:text-gray-800 hover:bg-stone-50"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${tab.icon} text-base`}></i>
                </div>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-8 lg:p-10">

            {/* ── 跨境手續 ── */}
            {activeTab === "border" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">跨境出行流程</h3>
                  <div className="space-y-6">
                    {borderSteps.map((s) => (
                      <div key={s.step} className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {s.step}
                        </div>
                        <div className="pt-1">
                          <p className="font-semibold text-gray-900 mb-1">{s.title}</p>
                          <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-orange-50 rounded-xl p-5 border border-orange-100">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-customer-service-2-line text-orange-500 text-xl"></i>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">全程代辦服務</p>
                    </div>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      所有跨境手續由我們專業團隊一手包辦——批文申請、臨時牌照、保證金、邊境通行證，讓您專心享受駕駛樂趣。
                    </p>
                  </div>

                  <div className="bg-stone-100 rounded-xl p-5">
                    <p className="font-semibold text-gray-900 text-sm mb-3">出發口岸</p>
                    <div className="flex items-center gap-3 py-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-2-line text-orange-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-gray-900 text-sm font-semibold">深圳灣口岸</p>
                        <p className="text-gray-500 text-xs mt-0.5">全天24小時通關 · 車輛專用通道</p>
                        <p className="text-gray-500 text-xs">香港屯門公路接駁，交通便利</p>
                      </div>
                    </div>
                    <div className="mt-2 pt-3 border-t border-stone-200">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-information-line text-orange-400 text-xs"></i>
                        </div>
                        <p className="text-gray-500 text-xs">建議提前30分鐘到達，確保順利過關</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-zinc-900 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-earth-line text-orange-400"></i>
                      </div>
                      <p className="font-semibold text-sm">歐洲路線</p>
                    </div>
                    <p className="text-zinc-400 text-xs leading-relaxed">
                      歐洲跨境手續複雜度更高，涉及多國簽注、車輛國際通行證等。我們安排專屬顧問逐一跟進，確保全程合規。
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── 車輛要求 ── */}
            {activeTab === "vehicle" && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">車輛準備要求</h3>
                <p className="text-gray-600 text-sm mb-8">不同路線對車輛有不同要求，提前做好準備是安全自駕的基礎</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {vehicleItems.map((item) => (
                    <div key={item.title} className="bg-stone-50 rounded-xl p-5 border border-stone-100 hover:border-orange-200 transition-colors">
                      <div className="w-11 h-11 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                        <i className={`${item.icon} text-orange-500 text-xl`}></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm">{item.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-amber-50 rounded-xl p-6 border border-amber-100">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-amber-500 text-2xl"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">重要提醒</p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        部分高原地區（西藏、青海海拔4000米以上）及歐洲冬季山口（斯泰爾維奧、格羅斯格洛克納）有強制要求：<strong className="text-gray-900">4WD越野車</strong>或<strong className="text-gray-900">安裝雪鏈/冬胎</strong>，違者可能被禁止通行或罰款。我們在行程確認後會逐一核查您的車輛是否符合要求。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── 國際駕照 ── */}
            {activeTab === "license" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">國際駕照申請指引</h3>
                  <div className="space-y-6">
                    {licenseSteps.map((s) => (
                      <div key={s.step} className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                          {s.step}
                        </div>
                        <div className="pt-1">
                          <p className="font-semibold text-gray-900 mb-1">{s.title}</p>
                          <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-orange-50 rounded-xl p-5 border border-orange-100">
                    <p className="font-semibold text-gray-900 text-sm mb-3">申請機構</p>
                    {[
                      { name: "香港汽車會", detail: "灣仔告士打道 2A-2B 號", tel: "2739 5273" },
                      { name: "香港駕駛學院", detail: "各分校均可辦理", tel: "2745 8888" },
                    ].map((org) => (
                      <div key={org.name} className="py-3 border-b border-orange-100 last:border-0">
                        <p className="text-gray-900 text-sm font-medium">{org.name}</p>
                        <p className="text-gray-500 text-xs mt-0.5">{org.detail}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-phone-line text-orange-400 text-xs"></i>
                          </div>
                          <span className="text-orange-500 text-xs">{org.tel}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-stone-100 rounded-xl p-5">
                    <p className="font-semibold text-gray-900 text-sm mb-3">IDP 快速資訊</p>
                    {[
                      { label: "收費", value: "約港幣 150 元" },
                      { label: "辦理時間", value: "2-3 個工作日" },
                      { label: "有效期", value: "12 個月" },
                      { label: "適用地區", value: "175+ 個國家及地區" },
                      { label: "配合使用", value: "須與香港駕照同時攜帶" },
                    ].map((row) => (
                      <div key={row.label} className="flex justify-between py-1.5 border-b border-stone-200 last:border-0">
                        <span className="text-gray-500 text-xs">{row.label}</span>
                        <span className="text-gray-900 text-xs font-medium">{row.value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-green-50 rounded-xl p-5 border border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-check-double-line text-green-500"></i>
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">我們全程協助</p>
                    </div>
                    <p className="text-gray-600 text-xs leading-relaxed">
                      報名確認後，我們顧問會提供完整申請清單並協助您核查所有文件是否齊全，出發前確保萬無一失。
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ── 行前準備 ── */}
            {activeTab === "preparation" && (
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">出發前準備清單</h3>
                <p className="text-gray-600 text-sm mb-8">依據路線難度，以下清單協助您系統性準備，確保安全出發</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {prepItems.map((cat) => (
                    <div key={cat.category} className="bg-stone-50 rounded-xl p-5 border border-stone-100">
                      <div className="flex items-center gap-2.5 mb-4">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cat.color}`}>
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm">{cat.category}</h4>
                      </div>
                      <ul className="space-y-2.5">
                        {cat.items.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-checkbox-circle-line text-orange-400 text-sm"></i>
                            </div>
                            <span className="text-gray-600 text-xs leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="mt-8 bg-zinc-900 rounded-xl p-6">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                      <p className="text-white font-bold text-base mb-1">準備好開始計劃你的自駕征途了嗎？</p>
                      <p className="text-zinc-400 text-sm">我們的顧問會在出發前4週安排專屬行前簡報，逐一核查所有準備事項</p>
                    </div>
                    <a
                      href="#contact"
                      onClick={(e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full text-sm font-semibold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer flex-shrink-0"
                    >
                      <span>立即諮詢</span>
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </section>
  );
};

export default SelfDriveInfoSection;
